import React, { memo } from 'react';
import { tileLabel, TILE_SPECIAL } from '../engine/type';

const ELEMENT_GLOW = {
  fire: 'rgba(255,59,48,0.75)',
  air: 'rgba(46,204,113,0.70)',
  water: 'rgba(79,195,247,0.75)',
  earth: 'rgba(141,110,99,0.75)',
  cosmic: 'rgba(255,215,0,0.60)',
};

// Empty-slot highlight (the free cell created after a 3-match)
// Make it VERY obvious: violet core + golden aura (premium).
const EMPTY_GOLD_BORDER = 'rgba(255,215,0,0.95)';
const EMPTY_BG =
  'radial-gradient(circle at 50% 50%, rgba(255,215,120,0.55), rgba(255,180,40,0.75) 55%, rgba(160,110,10,0.85) 100%)';

const TILEVIEW_CSS_ID = 'zcTileViewCss';
const TILEVIEW_CSS = `
@keyframes zcHintPulse {
  0%,100% { transform: scale(1); }
  50% { transform: scale(1.04); }
}

@keyframes zcEmptyGlow {
  0%,100% { transform: translateZ(0) scale(1); filter: brightness(1.06) saturate(1.25); }
  50% { transform: translateZ(0) scale(1.06); filter: brightness(1.22) saturate(1.45); }
}
`;

let _tileViewCssInjected = false;
function ensureTileViewCssOnce() {
  if (_tileViewCssInjected) return;
  if (typeof document === 'undefined') return;
  _tileViewCssInjected = true;
  try {
    if (document.getElementById(TILEVIEW_CSS_ID)) return;
    const style = document.createElement('style');
    style.id = TILEVIEW_CSS_ID;
    style.textContent = TILEVIEW_CSS;
    document.head.appendChild(style);
  } catch {
    // ignore
  }
}

// Try to inject immediately in the browser (avoids doing it during a move/render)
try { ensureTileViewCssOnce(); } catch { /* ignore */ }

let _reducedMotionCached = false;
let _reducedMotionValue = false;
function isReducedMotion() {
  if (_reducedMotionCached) return _reducedMotionValue;
  _reducedMotionCached = true;
  if (typeof window === 'undefined' || !window.matchMedia) {
    _reducedMotionValue = false;
    return _reducedMotionValue;
  }
  try {
    _reducedMotionValue = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    _reducedMotionValue = false;
  }
  return _reducedMotionValue;
}

function TileView({ tile, selected, hint, element: elementProp }) {
  // Ensure keyframes exist (injected once at module init)
  ensureTileViewCssOnce();

  // NOTE: in ZodiacCrush the “free cell” may come as null OR as a placeholder object.
  // Detect both, so the empty slot highlight always shows (especially on mobile).
  const empty =
    !tile ||
    tile === null ||
    tile === undefined ||
    tile?.isEmpty === true ||
    tile?.empty === true ||
    tile?.isHole === true ||
    tile?.hole === true ||
    String(tile?.type || '').toLowerCase() === 'empty' ||
    String(tile?.kind || '').toLowerCase() === 'empty' ||
    String(tile?.key || '').toLowerCase() === 'empty' ||
    String(tile?.key || '').toLowerCase() === 'hole' ||
    String(tile?.special || '').toLowerCase() === 'empty' ||
    String(tile?.special || '').toLowerCase() === 'hole' ||
    String(tile?.glyph || '') === '+' ||
    String(tile?.emoji || '') === '+' ||
    String(tile?.icon || '') === '+';

  // Avoid running tileLabel on placeholder/empty tiles
  let labelRaw = '';
  if (!empty && tile) {
    try {
      labelRaw = tileLabel(tile);
    } catch {
      labelRaw = '';
    }
  }
  const label =
    labelRaw ||
    tile?.glyph ||
    tile?.emoji ||
    tile?.icon ||
    tile?.sign ||
    tile?.zodiacSign ||
    tile?.zodiac ||
    tile?.zodiacName ||
    '';

  // Element: if BoardView passes `element`, do NOT infer/normalize (saves CPU)
  let element = elementProp || (empty ? 'cosmic' : tile?.element);
  const special = empty ? null : tile?.special;
  const glow = ELEMENT_GLOW[element] || ELEMENT_GLOW.cosmic;

  const border = empty
    ? `3px solid ${EMPTY_GOLD_BORDER}`
    : selected
      ? `2px solid ${glow}`
      : hint
        ? `2px dashed ${glow}`
        : '1px solid rgba(255,255,255,0.14)';

  const boxShadow = empty
    ? `0 0 0 1px rgba(255,255,255,0.14),
       0 0 38px rgba(180,90,255,0.95),
       0 0 70px rgba(180,90,255,0.55),
       0 26px 52px rgba(0,0,0,0.55),
       inset 0 1px 0 rgba(255,255,255,0.18),
       inset 0 -22px 34px rgba(0,0,0,0.6)`
    : selected
      ? `0 0 0 2px rgba(255,255,255,0.22), 0 18px 34px ${glow}, inset 0 1px 0 rgba(255,255,255,0.30), inset 0 -10px 18px rgba(0,0,0,0.35)`
      : hint
        ? `0 0 0 1px rgba(255,255,255,0.14), 0 14px 26px rgba(0,0,0,0.40), 0 0 22px ${glow}, inset 0 1px 0 rgba(255,255,255,0.22)`
        : '0 14px 26px rgba(0,0,0,0.46), inset 0 1px 0 rgba(255,255,255,0.22), inset 0 -10px 18px rgba(0,0,0,0.32)';

  const reduced = isReducedMotion();
  const fontSize = special ? 24 : 22;

  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        borderRadius: 16,
        border,
        background: empty ? EMPTY_BG : 'transparent',
        boxShadow,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        pointerEvents: 'none',
        userSelect: 'none',
        color: empty ? 'rgba(235,220,255,0.98)' : 'rgba(255,255,255,0.96)',
        fontSize: empty ? 18 : fontSize,
        lineHeight: 1,
        transform: empty ? 'translateZ(0) scale(1.02)' : selected ? 'translateZ(0) scale(1.10)' : hint ? 'translateZ(0) scale(1.05)' : 'translateZ(0) scale(1)',
        transition: 'transform 140ms ease, filter 140ms ease',
        animation: empty && !reduced ? 'zcEmptyGlow 850ms ease-in-out infinite' : undefined,
        position: 'relative',
        overflow: 'hidden',
        contain: 'layout paint',
        willChange: 'transform',
        backfaceVisibility: 'hidden',
      }}
    >
      {empty && (
        <>
          {/* Direction arrows – premium gold */}
          <span style={{
            position: 'absolute',
            top: 6,
            left: '50%',
            transform: 'translateX(-50%)',
            fontSize: 14,
            fontWeight: 900,
            color: '#000000',
            textShadow: '0 0 2px rgba(255,255,255,0.95), 0 0 4px rgba(255,255,255,0.85)',
          }}>▲</span>

          <span style={{
            position: 'absolute',
            right: 6,
            top: '50%',
            transform: 'translateY(-50%)',
            fontSize: 14,
            fontWeight: 900,
            color: '#000000',
            textShadow: '0 0 2px rgba(255,255,255,0.95), 0 0 4px rgba(255,255,255,0.85)',
          }}>▶</span>

          <span style={{
            position: 'absolute',
            bottom: 6,
            left: '50%',
            transform: 'translateX(-50%)',
            fontSize: 14,
            fontWeight: 900,
            color: '#000000',
            textShadow: '0 0 2px rgba(255,255,255,0.95), 0 0 4px rgba(255,255,255,0.85)',
          }}>▼</span>

          <span style={{
            position: 'absolute',
            left: 6,
            top: '50%',
            transform: 'translateY(-50%)',
            fontSize: 14,
            fontWeight: 900,
            color: '#000000',
            textShadow: '0 0 2px rgba(255,255,255,0.95), 0 0 4px rgba(255,255,255,0.85)',
          }}>◀</span>

        </>
      )}

      {hint && !empty && !selected && (
        <span
          style={{
            position: 'absolute',
            inset: 4,
            borderRadius: 14,
            border: `1px solid ${glow}`,
            boxShadow: `0 0 0 1px ${glow}, 0 0 26px ${glow}`,
            animation: reduced ? undefined : 'zcHintPulse 900ms ease-in-out infinite',
          }}
        />
      )}

      {!empty &&
        (special === TILE_SPECIAL.STAR ||
          special === TILE_SPECIAL.STRIPE_H ||
          special === TILE_SPECIAL.STRIPE_V) && (
          <span
            style={{
              position: 'absolute',
              top: 6,
              right: 6,
              width: 18,
              height: 18,
              borderRadius: 999,
              background: `radial-gradient(circle at 30% 30%, rgba(255,255,255,0.75), ${glow} 45%, rgba(0,0,0,0.75))`,
              boxShadow: `0 6px 14px rgba(0,0,0,0.35), 0 0 14px ${glow}`,
              border: '1px solid rgba(255,255,255,0.18)',
              opacity: 0.9,
            }}
          />
        )}

      {empty ? null : (
        <span
          style={{
            filter: 'drop-shadow(0 6px 14px rgba(0,0,0,0.55))',
            textShadow: '0 2px 10px rgba(0,0,0,0.55)',
            fontFamily: '"Apple Color Emoji","Segoe UI Emoji","Noto Color Emoji",system-ui,sans-serif',
          }}
        >
          {label}
        </span>
      )}
    </div>
  );
}

export default memo(
  TileView,
  (prev, next) => {
    if (prev.selected !== next.selected) return false;
    if (prev.hint !== next.hint) return false;
    if ((prev.element || '') !== (next.element || '')) return false;

    const a = prev.tile;
    const b = next.tile;
    if (a === b) return true;
    if (!a && !b) return true;
    if (!a || !b) return false;

    const aId = a._id || a.id;
    const bId = b._id || b.id;
    if (aId !== bId) return false;

    const aSpec = a.special;
    const bSpec = b.special;
    if (aSpec !== bSpec) return false;

    const aGlyph = a.glyph || a.emoji || a.icon || a.sign || a.zodiacSign || a.zodiac || a.zodiacName || '';
    const bGlyph = b.glyph || b.emoji || b.icon || b.sign || b.zodiacSign || b.zodiac || b.zodiacName || '';
    return aGlyph === bGlyph;
  }
);